/**
 * API error normalization.
 *
 * Every failed API call used to reach the UI as
 * `API error: 403 {"statusCode":403,"error":"MFA_REQUIRED",...}` — the raw
 * status line plus the verbatim response body. Roughly 130 call sites render
 * `err.message` straight into a toast, so that string *was* the product.
 *
 * ApiError keeps `message` fit to show a user and parks the technical detail
 * on `status` / `code` / `detail` for logs and for the handful of callers that
 * branch on a specific failure.
 */

/** Shape NestJS uses for HttpException responses. */
interface NestErrorBody {
  statusCode?: number;
  /** Human-readable text, or an array of them from ValidationPipe. */
  message?: string | string[];
  /** Either a machine code (MFA_REQUIRED) or a status name ("Forbidden"). */
  error?: string;
}

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never got a response. */
  readonly status: number;
  /** Machine-readable code from the backend, when it sent one. */
  readonly code?: string;
  /** Raw server text. For logs and debugging — never render this. */
  readonly detail?: string;

  constructor(
    message: string,
    options: { status: number; code?: string; detail?: string }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.detail = options.detail;
  }
}

const GENERIC_SERVER_ERROR =
  'Something went wrong on our end. Please try again in a moment.';

/**
 * Fallbacks used when the backend sends no usable message of its own.
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: "Some of the information sent isn't valid. Please review it and try again.",
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  408: 'The request took too long. Please try again.',
  409: 'That conflicts with something that already exists.',
  413: 'That file is too large to upload.',
  415: "That file type isn't supported.",
  422: "Some of the information sent isn't valid. Please review it and try again.",
  429: 'Too many attempts. Please wait a moment and try again.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The request took too long. Please try again.',
};

/**
 * Backend codes that deserve wording better than their raw server message.
 * Anything not listed falls through to the server's own message, which for
 * this API is already written for humans ("Invalid or expired verification
 * code", "Your access to this tenant has been deactivated").
 */
const CODE_MESSAGES: Record<string, string> = {
  MFA_REQUIRED:
    'Please enter the verification code we emailed you to continue.',
  MFA_GUARD_MISCONFIGURED: GENERIC_SERVER_ERROR,
};

/**
 * Codes that are handled by a global mechanism, not by whichever component
 * happened to catch the failed request. MFA_REQUIRED already flips
 * `mfaRequired` via the handler registered in api.ts, and the router
 * redirects to /mfa-verify on the next render — so toUserMessage() must NOT
 * let this code override a caller's own fallback (see below), or an
 * unrelated hook that simply raced the MFA gate (e.g. a profile fetch fired
 * the instant a route mounts) ends up showing "Please enter the
 * verification code..." inside its own unrelated error UI, sometimes as a
 * dead end with no way back to /mfa-verify.
 */
const AUTH_FLOW_ONLY_CODES = new Set(['MFA_REQUIRED']);

/**
 * Server messages that are technically strings but say nothing to a user.
 * Treated as absent so the status fallback wins.
 */
const OPAQUE_MESSAGES = new Set([
  'internal server error',
  'error',
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'conflict',
  'unprocessable entity',
  'too many requests',
  'service unavailable',
]);

function isUsefulServerMessage(text: string | undefined): text is string {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (OPAQUE_MESSAGES.has(trimmed.toLowerCase())) return false;
  // Stack traces and raw SQL/Prisma dumps are long and start leaking
  // internals; never put those in front of a user.
  if (trimmed.length > 300) return false;
  if (trimmed.includes('\n    at ')) return false;
  return true;
}

/** Parse a response body as a Nest error envelope, tolerating non-JSON. */
function parseBody(raw: string): NestErrorBody | null {
  if (!raw.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object'
      ? (parsed as NestErrorBody)
      : null;
  } catch {
    return null;
  }
}

/**
 * Build an ApiError from a non-OK response body.
 *
 * @param status - HTTP status code
 * @param rawBody - Response body as text (may be empty or non-JSON)
 */
export function buildApiError(status: number, rawBody: string): ApiError {
  const body = parseBody(rawBody);

  // `error` carries a machine code only when it isn't just the status name.
  const rawCode = typeof body?.error === 'string' ? body.error : undefined;
  const code = rawCode && /^[A-Z][A-Z0-9_]+$/.test(rawCode) ? rawCode : undefined;

  const serverMessage = Array.isArray(body?.message)
    ? body?.message.filter((m) => typeof m === 'string').join('. ')
    : body?.message;

  let message: string;
  if (code && CODE_MESSAGES[code]) {
    message = CODE_MESSAGES[code];
  } else if (status >= 500) {
    // 5xx messages are for operators, not users, and are the most likely to
    // leak an ORM or driver detail.
    message = STATUS_MESSAGES[status] ?? GENERIC_SERVER_ERROR;
  } else if (isUsefulServerMessage(serverMessage)) {
    message = serverMessage;
  } else {
    message = STATUS_MESSAGES[status] ?? GENERIC_SERVER_ERROR;
  }

  return new ApiError(message, {
    status,
    code,
    detail: rawBody.slice(0, 2000) || undefined,
  });
}

/**
 * Wrap a transport-level failure (DNS, offline, CORS, abort) — cases where
 * there is no HTTP response at all, so `status` is 0.
 */
export function buildNetworkError(cause: unknown): ApiError {
  const isAbort =
    cause instanceof DOMException && cause.name === 'AbortError';

  return new ApiError(
    isAbort
      ? 'The request took too long. Please check your connection and try again.'
      : "We couldn't reach the server. Please check your connection and try again.",
    {
      status: 0,
      code: isAbort ? 'REQUEST_TIMEOUT' : 'NETWORK_ERROR',
      detail: cause instanceof Error ? cause.message : String(cause),
    }
  );
}

/**
 * Built-in error types that only ever mean "we have a bug". Their messages
 * ("Cannot read properties of undefined (reading 'id')") are short and
 * newline-free, so no heuristic on the text catches them — the type is the
 * only reliable signal.
 */
const RUNTIME_ERROR_NAMES = new Set([
  'TypeError',
  'ReferenceError',
  'SyntaxError',
  'RangeError',
  'EvalError',
  'URIError',
]);

/**
 * Resolve any thrown value to something safe to show a user.
 *
 * Use at display sites so a non-API error (a bug, a rejected string) can't
 * put a stack trace or an internal identifier in a toast. Replaces the
 * `err instanceof Error ? err.message : 'fallback'` idiom, which trusted
 * every Error message verbatim.
 *
 * @param err - The caught value
 * @param fallback - Message to use when nothing better can be derived
 */
export function toUserMessage(
  err: unknown,
  fallback = GENERIC_SERVER_ERROR
): string {
  // ApiError messages are built by this module and always presentable —
  // except the auth-flow-only codes, which belong to the global handler in
  // api.ts, not to whatever component happened to catch this rejection.
  if (err instanceof ApiError) {
    if (err.code && AUTH_FLOW_ONLY_CODES.has(err.code)) return fallback;
    return err.message;
  }

  if (err instanceof Error) {
    if (RUNTIME_ERROR_NAMES.has(err.name)) return fallback;
    // A plain `new Error("...")` in this codebase is a deliberately authored
    // message; keep it unless it looks like a dump.
    if (isUsefulServerMessage(err.message)) return err.message;
  }

  return fallback;
}
